import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  Link,
  Spinner,
  useToast,
  Button,
  IconButton,
  HStack,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { useSortBy, useTable } from 'react-table';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import PromotionsApi from '../../../../api/promotions';
import { prettifyDate } from '../../../../functions/dateUtils';
import PromotionFormModal from './PromotionFormModal';

const InAppPromotionsModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [promotionToDelete, setPromotionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const toast = useToast();

  const {
    isOpen: isFormModalOpen,
    onOpen: onFormModalOpen,
    onClose: onFormModalClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();

  const cancelRef = React.useRef();

  const columns = useMemo(
    () => [
      {
        Header: 'פעולות',
        accessor: 'actions',
        width: '100px',
        Cell: ({ row }) => (
          <HStack spacing={2}>
            <IconButton
              icon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              variant="ghost"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.original);
              }}
            />
            <IconButton
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.original);
              }}
            />
          </HStack>
        ),
      },
      {
        Header: 'ID',
        accessor: 'id',
        width: '150px',
        Cell: ({ value }) => (
          <Text fontSize="xs" noOfLines={1} title={value}>
            {value}
          </Text>
        ),
      },
      {
        Header: 'כותרת',
        accessor: 'title',
        width: '120px',
      },
      {
        Header: 'תוכן',
        accessor: 'body',
        width: '180px',
        Cell: ({ value }) => (
          <Text fontSize="sm" noOfLines={2}>
            {value || '-'}
          </Text>
        ),
      },
      {
        Header: 'Web URL',
        accessor: 'webUrl',
        width: '120px',
        Cell: ({ value }) => value ? (
          <Link href={value} isExternal color="blue.500" fontSize="sm" noOfLines={1}>
            Link
          </Link>
        ) : '-',
      },
      {
        Header: 'Trigger',
        accessor: 'triggerType',
        width: '100px',
      },
      {
        Header: 'Variant',
        accessor: 'variant',
        width: '100px',
      },
      {
        Header: 'חברה',
        accessor: 'companyId',
        width: '80px',
        Cell: ({ value }) => (
          <Badge colorScheme={value ? 'blue' : 'green'}>
            {value ? value : 'גלובלי'}
          </Badge>
        ),
      },
      {
        Header: 'פעם אחת',
        accessor: 'showOnce',
        width: '80px',
        Cell: ({ value }) => (
          <Badge colorScheme={value ? 'green' : 'gray'}>
            {value ? 'כן' : 'לא'}
          </Badge>
        ),
      },
      {
        Header: 'תאריך התחלה',
        accessor: 'startDate',
        width: '100px',
        Cell: ({ value }) => value ? prettifyDate(value) : '-',
      },
      {
        Header: 'תאריך סיום',
        accessor: 'endDate',
        width: '100px',
        Cell: ({ value }) => value ? prettifyDate(value) : '-',
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
    },
    useSortBy
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await PromotionsApi.getAllPromotions();
      if (response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast({
        title: 'שגיאה בטעינת נתוני פרומושנים',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const handleCreate = () => {
    setSelectedPromotion(null);
    onFormModalOpen();
  };

  const handleEdit = (promotion) => {
    setSelectedPromotion(promotion);
    onFormModalOpen();
  };

  const handleDeleteClick = (promotion) => {
    setPromotionToDelete(promotion);
    onDeleteAlertOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!promotionToDelete) return;

    setDeleteLoading(true);
    try {
      await PromotionsApi.deletePromotion(promotionToDelete.id);
      toast({
        title: 'פרומושן נמחק בהצלחה',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting promotion:', error);
      toast({
        title: 'שגיאה במחיקת פרומושן',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleteLoading(false);
      setPromotionToDelete(null);
      onDeleteAlertClose();
    }
  };

  const handleFormSuccess = () => {
    fetchData();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="95vw" maxH="90vh">
          <ModalHeader>
            <Flex justify="space-between" align="center">
              <Heading size="lg" color={textColor}>
                In-App Promotions
              </Heading>
              <HStack spacing={4} mr="40px">
                <Badge colorScheme="purple" fontSize="md">
                  {data.length} פרומושנים
                </Badge>
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  size="sm"
                  onClick={handleCreate}
                >
                  פרומושן חדש
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <Card>
              <Flex direction="column" w="100%">
                {/* Results count */}
                <Text mb={4} fontSize="sm" color="gray.600">
                  סה"כ {data.length} פרומושנים באפליקציה
                </Text>

                {loading ? (
                  <Flex justify="center" align="center" height="200px">
                    <Spinner size="lg" />
                  </Flex>
                ) : (
                  /* Table */
                  <Box overflowX="auto">
                    <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
                      <Thead>
                        {headerGroups.map((headerGroup, index) => (
                          <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                            {headerGroup.headers.map((column, index) => (
                              <Th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                pe="10px"
                                key={index}
                                borderColor={borderColor}
                              >
                                <Flex
                                  justify="space-between"
                                  align="center"
                                  fontSize={{ sm: '10px', lg: '12px' }}
                                  color="gray.400"
                                >
                                  {column.render('Header')}
                                </Flex>
                              </Th>
                            ))}
                          </Tr>
                        ))}
                      </Thead>
                      <Tbody {...getTableBodyProps()}>
                        {rows.map((row, index) => {
                          prepareRow(row);
                          return (
                            <Tr {...row.getRowProps()} key={index}>
                              {row.cells.map((cell, index) => (
                                <Td
                                  {...cell.getCellProps()}
                                  key={index}
                                  fontSize={{ sm: '14px' }}
                                  minW={{ sm: '80px', md: '100px', lg: 'auto' }}
                                  borderColor="transparent"
                                >
                                  <Text color={textColor} fontSize="sm" fontWeight="500">
                                    {cell.render('Cell')}
                                  </Text>
                                </Td>
                              ))}
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {!loading && data.length === 0 && (
                  <Flex justify="center" align="center" height="100px">
                    <Text color="gray.500">אין פרומושנים. לחץ על "פרומושן חדש" כדי ליצור אחד.</Text>
                  </Flex>
                )}
              </Flex>
            </Card>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Create/Edit Form Modal */}
      <PromotionFormModal
        isOpen={isFormModalOpen}
        onClose={onFormModalClose}
        promotion={selectedPromotion}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              מחיקת פרומושן
            </AlertDialogHeader>

            <AlertDialogBody>
              האם אתה בטוח שברצונך למחוק את הפרומושן "{promotionToDelete?.title}"?
              <br />
              פעולה זו אינה ניתנת לביטול.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                ביטול
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={deleteLoading}
                loadingText="מוחק..."
              >
                מחק
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default InAppPromotionsModal;
